/**
 * The MSM brand tokens, in the one place both the web app and the API can
 * read them.
 *
 * WHY THEY LIVE HERE AND NOT IN apps/web/src/theme.ts, where they started:
 * notification email has to look like the site, and the API composes that
 * email. Email cannot import an AntD theme — it cannot import React at all —
 * so without a shared home the palette would exist twice and drift the first
 * time one copy changed. `apps/web/src/theme.ts` now re-exports these, so
 * every existing web import still resolves and there is still exactly one
 * definition.
 *
 * The colours themselves are the company's, lifted from the corporate site
 * (`MSM Real Estate/frontend/src/styles/tokens.css` and `brand/PALETTE.md`):
 * three cool near-neutrals from the logo, plus white. There is no black —
 * `ink` is the black.
 *
 *   ink   #262042  the Latin wordmark. Type, primary buttons, links.
 *   navy  #1B2A48  the dark artboard. The sidebar, and the email header.
 *   haze  #76759B  the Arabic wordmark. Hairlines, rules, quiet text.
 *   paper #FFFFFF  the page.
 *
 * Each ramp's base sits at 600, with steps mixed toward white (50–500) and
 * toward black (700–950), so a component always has a lighter border or a
 * darker hover to reach for without inventing a hue.
 */

export const ink = {
  50: '#f6f6f7',
  100: '#eeedf0',
  200: '#d8d7dd',
  300: '#bab8c3',
  400: '#8e8b9d',
  500: '#5c5871',
  600: '#262042',
  700: '#201b38',
  800: '#1b162e',
  900: '#141122',
  950: '#0d0b16',
} as const;

export const navy = {
  50: '#f6f6f8',
  100: '#edeef0',
  200: '#d6d9de',
  300: '#b6bbc4',
  400: '#8890a0',
  500: '#545f76',
  600: '#1b2a48',
  700: '#17243d',
  800: '#131d32',
  900: '#0e1625',
  950: '#090e18',
  /** navy lifted 8% and 16% toward white — the dark theme's raised surfaces. */
  raised: '#2d3b57',
  raisedMore: '#3f4c65',
} as const;

export const haze = {
  50: '#faf9fb',
  100: '#f4f4f7',
  200: '#e6e6ed',
  300: '#d3d3df',
  400: '#b8b7cb',
  500: '#9898b4',
  600: '#76759b',
  700: '#646384',
  800: '#53526c',
  900: '#3d3d51',
  950: '#282835',
} as const;

/**
 * Feedback hues.
 *
 * The brand palette has none — it is three cool near-neutrals — so these are
 * system hues pulled down in chroma to sit beside it and dark enough to carry
 * white text. They are the exact values the corporate site uses, converted
 * from its OKLCH declarations, and appear only where the interface has
 * something to say about state.
 */
export const feedback = {
  info: '#305d96',
  success: '#236b4a',
  warning: '#8b640f',
  error: '#9b1e22',
} as const;

/**
 * The font stack, shared so an email renders in the same face as the screen
 * where the client allows it.
 *
 * Email clients mostly will NOT load a web font, so in practice this falls
 * through to the platform's Arabic face — which is exactly why the stack is
 * worth sharing rather than each surface inventing its own fallback order.
 * Digits stay Latin; the finance team reads them that way.
 */
export const FONT_STACK =
  "'IBM Plex Sans Arabic', 'Tajawal', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans Arabic', Tahoma, Arial, sans-serif";

/** Reminder severity, ordered most urgent first. The digest sorts by this. */
export const SEVERITY_ORDER = ['critical', 'high', 'normal', 'low'] as const;
export type Severity = (typeof SEVERITY_ORDER)[number];

export const SEVERITY_LABEL_AR: Record<string, string> = {
  critical: 'حرج',
  high: 'مرتفع',
  normal: 'عادي',
  low: 'منخفض',
};

/**
 * AntD preset tag colour names, as the Reminders and Dashboard screens use
 * them. Kept as names here because that is what `<Tag color=…>` takes.
 * `SEVERITY_TAG_HEX` below is the same thing resolved for email, which has
 * no component to hand the name to.
 */
export const SEVERITY_COLOR: Record<string, string> = {
  low: 'default',
  normal: 'blue',
  high: 'orange',
  critical: 'red',
};

/**
 * THE APPLICATION'S PALETTE — graphite and mint (chosen 2026-09-30).
 *
 * Ghassan was shown seven palettes on a miniature of the dashboard and chose
 * this one: a near-black graphite that leads (type, primary actions, the hero
 * card, the story column) and ONE accent, a mint, that marks what is current,
 * selected or positive. Everything else is the graphite ramp's greys.
 *
 * ink, navy and haze above are NOT replaced. They are the corporate site's
 * colours and the notification email is still composed from them, so the
 * email keeps looking like the site; the screen now has its own palette, the
 * same arrangement `feedback.warning` already had with the theme.
 *
 * Both ramps computed in OKLCH — graphite at 260° with almost no chroma (a
 * cool neutral, so it never reads as brown), mint at 167° — so the steps are
 * even to the eye. Text on white: graphite 500 and darker, mint 700 and darker.
 */
export const graphite = {
  50: '#f7f8f9',
  100: '#f0f1f3',
  200: '#e4e6e9',
  300: '#cdcfd4',
  400: '#a3a6ab',
  /* Darkened from #727579 by the 2026-09-30 audit: quiet text in this step
     measured 4.15:1 on the tinted canvas, under the 4.5 floor. */
  500: '#66696e',
  600: '#55585d',
  700: '#3b3d41',
  800: '#24272a',
  900: '#15171b',
} as const;

export const mint = {
  50: '#ecfbf4',
  100: '#d9f7ea',
  200: '#b3efd6',
  300: '#87e0bd',
  400: '#51caa0',
  500: '#18b287',
  600: '#009d74',
  700: '#007c5b',
  800: '#035b43',
  900: '#0d3f2f',
} as const;

/**
 * The categorical palette: mint, graphite, light mint, grey. The accent leads —
 * the first series is the one the reader is meant to follow.
 *
 * Four hues was what this started as (indigo, teal, rose, violet), and a teal
 * bar beside a teal «ساري» chip said a property was healthy when it meant
 * nothing. Now the two families ARE the palette, stepped in lightness, and
 * lightness is also what survives colour-vision deficiency and greyscale
 * print. Adjacent steps sit ≥ 0.12 apart in OKLCH L. Legal only because every
 * chart here labels its marks directly or carries a key with the figures.
 */
export const series = [mint[600], graphite[700], mint[300], graphite[300]] as const;

/**
 * TWO FAMILIES, NO THIRD HUE.
 *
 * The screen is graphite and mint and nothing else. Severity is not a colour
 * of its own — it is DEPTH: light grey, darker grey, black. Good and current
 * are mint. Unknown is a hatch. This is what the reference dashboards do
 * (Donezo is greens and black; Sypher greens, greys and black) and it is what
 * "synergy" meant: every mark on the page drawn from the one palette.
 *
 * It took three wrong answers to get here, and they are recorded so nobody
 * reintroduces them: a PLUM for caution ("ugly"), then a lighter CRIMSON for
 * caution beside a crimson bad, with a crimson-rose ageing ramp ("this pink
 * color still there!!!"). Any red that sits next to mint reads as pink —
 * mint's complement IS pink — so no red can live in this palette at all.
 */

/**
 * Arrears ageing, as a scale rather than as six unrelated tags.
 *
 * `current` is mint: it is money that is not late, the palette's "fine". The
 * four overdue steps are graphite darkening with age, ending in black at
 * 90+, so the shape of a stacked bar reads as severity in colour, in
 * greyscale print, and to any colour-vision deficiency alike.
 *
 * `unaged` is the honest one. Sixteen opening balances carry
 * `aging_status: due_date_unverified`; they are not current and they are not
 * 90+, they are unknown. It renders HATCHED — visibly not a value — because
 * dropping it into the worst bucket would overstate the figure a lender reads
 * hardest on the basis of missing data, and dropping it into `current` would
 * hide it. See services/reports.ts.
 */
export const ageing = {
  current: mint[200],
  d1_30: graphite[300],
  d31_60: graphite[500],
  d61_90: graphite[700],
  d90_plus: graphite[900],
  unaged: graphite[300],
} as const;

/**
 * State, for the things that have a state rather than a quantity: a unit is
 * occupied or vacant, a lease is registered in إيجار or it is not.
 *
 * Never used without its label beside it: caution and bad differ in depth,
 * not hue, so the word and the dot carry the state and the depth ranks it.
 */
export const state = {
  good: mint[700],
  warning: graphite[500],
  bad: graphite[900],
  /** Not "zero" — "we do not know". Always drawn hatched. */
  unknown: graphite[400],
  /** The de-emphasised remainder of a whole: the empty part of a meter. */
  track: graphite[200],
} as const;

/**
 * A severity chip, resolved to literal colours for email, which has no
 * stylesheet to hand a class to. The same chip the screen draws (`.chip` in
 * apps/web/src/styles.css): a small grey pill whose only colour is a dot. The
 * one red in the whole system is the critical dot; every other level is a
 * depth of graphite.
 *
 * These were AntD's preset tag hues — a red, an orange and a blue slab — from
 * when the screen still drew them. The screen moved to graphite and mint on
 * 2026-09-30 and the email did not, until Ghassan asked for the two to be
 * consolidated (2026-10-04).
 */
export const SEVERITY_CHIP: Record<string, { bg: string; text: string; dot: string }> = {
  critical: { bg: graphite[100], text: graphite[900], dot: '#d92d20' },
  high: { bg: graphite[100], text: graphite[800], dot: graphite[700] },
  normal: { bg: graphite[100], text: graphite[700], dot: graphite[400] },
  low: { bg: graphite[100], text: graphite[600], dot: graphite[300] },
};
